import {eq, isEmpty} from '@shared/helpers/lang'

import {
    BankCodeType,
    FinancialInvestmentCompanyCodeType,
    NormalPayMethods,
    PaymentsCardCodeType,
    PaymentTabMethod,
    PaymentTabMethodType,
    PayMethod,
    PayMethodType,
} from '@web/core/constants'
import {getCardCodeName} from '@web/helpers/policies'
import {AccountView} from '@web/slices/account/slice'
import {CardView, getCommonInstallments} from '@web/slices/card/slice'

import {BankEasyPayItem, CardEasyPayItem} from '../interfaces'

/** 선택된 결제수단 tab 에 따른 결제수단 선택을 반환합니다 */
export function getNextPayMethod({
    selectedPayMethodTab,
    currentPayMethods,
    availableTypes,
    defaultNormalPayMethod,
}: {
    selectedPayMethodTab: PaymentTabMethodType
    currentPayMethods: PayMethodType[]
    availableTypes: PayMethodType[]
    defaultNormalPayMethod: Maybe<PayMethodType>
}): PayMethodType {
    const eqPayMethod = eq(selectedPayMethodTab)
    if (eqPayMethod(PaymentTabMethod.RECHARGE)) {
        // 충전 결제
        return PayMethod.RECHARGE_POINT
    } else if (eqPayMethod(PaymentTabMethod.EASY_CARD)) {
        // 간편카드결제
        return PayMethod.CARD_EASY_PAY
    } else if (eqPayMethod(PaymentTabMethod.EASY_ACCOUNT)) {
        // 간편계좌결제
        return PayMethod.BANK_EASY_PAY
    } else if (eqPayMethod(PaymentTabMethod.NORMAL)) {
        const currentPayMethod = currentPayMethods.find((pay) => NormalPayMethods.includes(pay))
        if (currentPayMethod) {
            return currentPayMethod
        }
        if (!isEmpty(availableTypes)) {
            /**
             * 최근 결제수단이 일반결제수단이였다면, 첫번째 일반 결제수단인 삼성페이 대신 일반결제수단 기본 선택
             * @ref https://oss.fin.navercorp.com/payOrder/plan/issues/1245
             **/
            if (defaultNormalPayMethod) {
                return defaultNormalPayMethod
            }
            const nextPayMethod = NormalPayMethods.find((payMethod) => availableTypes.includes(payMethod))
            if (nextPayMethod) {
                return nextPayMethod
            }
        }
    }
    return PayMethod.NONE
}

export const convertAccount = (bankEasyPays: BankEasyPayItem[]): AccountView[] => {
    return bankEasyPays.map((bankEasyPay) => {
        return {
            accountKey: bankEasyPay.accountKey,
            associatedCheckCard: bankEasyPay.associatedCheckCard,
            bankCode: bankEasyPay.bankCode as BankCodeType | FinancialInvestmentCompanyCodeType,
            bankName: bankEasyPay.bankName,
            maskedAccountNumber: bankEasyPay.maskedAccountNumber,
            nickName: bankEasyPay.nickName,
            payPartnershipItemCode: bankEasyPay.payPartnershipItemCode,
            payPartnershipItemName: bankEasyPay.payPartnershipItemName,
            payServiceMaintenanceSchedule: bankEasyPay.payServiceMaintenanceSchedule,
            payable: bankEasyPay.payable,
            pendingPayable: bankEasyPay.pendingPayable,
            registrationCompletedDateTime: bankEasyPay.registrationCompletedDateTime,
            openBankingAgreed: true,
        }
    })
}

export const convertCard = (cardEasyPays: CardEasyPayItem[]): CardView[] => {
    return cardEasyPays.map((cardEasyPay) => {
        const companyCode = cardEasyPay.cardCompanyCode as PaymentsCardCodeType
        return {
            category: cardEasyPay.cardCategory,
            companyCode,
            companyId: cardEasyPay.cardCompanyId,
            companyName: getCardCodeName(companyCode) + '카드',
            id: cardEasyPay.cardId,
            imageUrl: cardEasyPay.cardImageUrl,
            name: cardEasyPay.cardName,
            ownerType: cardEasyPay.cardOwnerType,
            productCode: cardEasyPay.cardProductCode,
            type: cardEasyPay.cardType ?? 'CREDIT',
            maskedNumber: cardEasyPay.maskedCardNumber,
            payService: {
                payable: cardEasyPay.payServiceAvailable,
                maintenance: cardEasyPay.payServiceMaintenanceSchedule,
                supportedMerchant: true,
            },
            longTermInstallment: cardEasyPay.longTermInstallment,
            registeredDateTime: cardEasyPay.registeredDateTime,
            reward: !cardEasyPay.rewardCard
                ? null
                : {
                      rewardAccumulateRate: cardEasyPay.rewardAccumulateRate,
                      rewardPointResidualAmount: cardEasyPay.rewardPointResidualAmount,
                      minAmount: 0,
                  },
            interestFreeBenefitPromotion: {
                isAvailable: true,
                minAmount: 0,
                month: 0,
            },
            plcc: cardEasyPay.hyundaiPlccCard
                ? {
                      type: 'NAVER_HYUNDAI',
                  }
                : null,
            issuerCode: cardEasyPay.cardIssuerCode,
            baseInstallmentInfo: getCommonInstallments(cardEasyPay.cardCompanyCode as PaymentsCardCodeType),
        }
    })
}
